"use client";

import React, { useCallback, useState } from "react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api";
import { UserPlus, Check, Clock, Loader2 } from "lucide-react";

export type FriendStatus = "none" | "pending" | "requested" | "friends";

export function FriendButton({
  personId,
  initialStatus = "none",
  size = "md",
  className = "",
  onChange,
  showDecisionActions = true,
  appearance = "default",
}: {
  personId: string | number;
  initialStatus?: FriendStatus;
  size?: "sm" | "md" | "lg";
  className?: string;
  onChange?: (status: FriendStatus) => void;
  /** When false, hides Accept/Decline UI for incoming requests (use header controls instead). */
  showDecisionActions?: boolean;
  /** 'scrim' renders a translucent white-on-image pill for use inside the profile banner. */
  appearance?: "default" | "scrim";
}) {
  const router = useRouter();
  const { data: session, status } = useSession();
  const [friendStatus, setFriendStatus] = useState<FriendStatus>(initialStatus);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const isAuthed = status === "authenticated";
  // WHO is signed in, as a string. The status check below keys on this, never on
  // the session object: next-auth hands back a new session object on every refetch
  // (every 5 minutes, on window focus, and whenever ANY other open tab refetches,
  // because it broadcasts across tabs). Keyed on the object, with getApiToken(session)
  // in a useMemo, each refetch forced a fresh /api/auth/session (the session was
  // read as getApiToken's forceRefresh flag) and re-fired GET /api/friendship/{id}:
  // with ~16 profile tabs open that was 16 calls per person per tick, ~1,000 requests
  // an hour from one admin browser, and 429s from the API (2026-10-08). apiFetch
  // supplies the bearer from the shared token cache that providers.tsx seeds.
  const viewerKey: string | null = isAuthed
    ? String((session?.user as any)?.id ?? (session?.user as any)?.email ?? "authed")
    : null;

  // Helper: detect id kind to choose correct endpoints
  const idStr = String(personId);
  const isNumericId = /^\d+$/.test(idStr);
  const isGuidId = /^[0-9a-fA-F-]{36}$/.test(idStr);

  // Initialize current status from API once per (viewer, person). Waits for an
  // authenticated session, so a guest never fires a 401 at the API.
  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!viewerKey) return;
      try {
        let s: FriendStatus | null = null;
        const url = isNumericId
          ? `/api/friendship/${encodeURIComponent(idStr)}`
          : isGuidId
          ? `/api/user/v2/${encodeURIComponent(idStr)}/friend`
          : null;
        if (!url) return;
        const res = await apiFetch(url, { method: 'GET', cache: 'no-store' });
        if (res.ok) {
          const json = await res.json().catch(() => ({}));
          s = (json?.friendStatus as FriendStatus) || (json?.status as FriendStatus) || null;
        }
        if (alive && s) setFriendStatus(s);
      } catch {}
    })();
    return () => { alive = false; };
  }, [viewerKey, idStr, isNumericId, isGuidId]);

  // Unified guest gate: same destination as Chat/Email/Watch (register with
  // returnTo), instead of the old signIn() bounce — one row, one behavior.
  const goRegister = useCallback(() => {
    const ret = typeof window !== 'undefined' ? window.location.pathname : '/';
    router.push(`/register?returnTo=${encodeURIComponent(ret)}`);
  }, [router]);

  const add = useCallback(async () => {
    if (!isAuthed) {
      goRegister();
      return;
    }
    setBusy(true);
    const prev = friendStatus;
    try {
      setFriendStatus("pending");
      // Choose endpoint by id kind
      const url = isNumericId
        ? `/api/friendship/${encodeURIComponent(idStr)}`
        : `/api/user/v2/${encodeURIComponent(idStr)}/friend`;
      const res = await apiFetch(url, { method: 'POST', cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      try {
        const json = await res.json();
        const next = (json?.friendStatus as FriendStatus) || (json?.status as FriendStatus) || "pending";
        setFriendStatus(next);
        onChange?.(next);
      } catch {
        onChange?.("pending");
      }
    } catch (e: any) {
      setFriendStatus(prev);
      toast({ title: 'Failed to send friend request', description: e?.message || '', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }, [isAuthed, idStr, isNumericId, onChange, friendStatus, goRegister, toast]);

  const removeOrCancel = useCallback(async () => {
    if (!isAuthed) {
      goRegister();
      return;
    }
    setBusy(true);
    const prev = friendStatus;
    try {
      // Optimistic: removing goes to none
      setFriendStatus("none");
      const url = isNumericId
        ? `/api/friendship/${encodeURIComponent(idStr)}`
        : `/api/user/v2/${encodeURIComponent(idStr)}/friend`;
      const res = await apiFetch(url, { method: 'DELETE', cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      try {
        const json = await res.json();
        const next = (json?.friendStatus as FriendStatus) || (json?.status as FriendStatus) || "none";
        setFriendStatus(next);
        onChange?.(next);
      } catch {
        onChange?.("none");
      }
    } catch (e: any) {
      setFriendStatus(prev);
      toast({ title: 'Failed to update friendship', description: e?.message || '', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }, [isAuthed, idStr, isNumericId, onChange, friendStatus, goRegister, toast]);

  if (!isAuthed && friendStatus === "friends") {
    // Shouldn't happen, but ensure safe label
  }

  const scrim = appearance === "scrim";
  const common = {
    size: size === "md" ? "md" : (size as any),
    disabled: busy,
    className: cn(
      "min-w-28",
      scrim && "bg-white/10 text-white ring-1 ring-white/25 hover:bg-white/20 hover:text-white backdrop-blur-sm",
      className
    ),
  } as const;

  if (friendStatus === "none") {
    return (
      <Button {...common} variant={scrim ? "ghost" : "primary"} aria-label="Add friend" onClick={add} aria-busy={busy}>
        {busy ? <Loader2 className="icon-20 animate-spin" /> : <UserPlus className="icon-20" />} {busy ? 'Sending…' : 'Add Friend'}
      </Button>
    );
  }

  if (friendStatus === "pending") {
    return (
      <Button
        {...common}
        variant="outline"
        aria-label="Friend request sent. Click to cancel"
        onClick={removeOrCancel}
        aria-busy={busy}
        className={cn("border-[color-mix(in_oklab,hsl(var(--ring))_40%,transparent)]")}
      >
        {busy ? <Loader2 className="icon-20 animate-spin" /> : <Clock className="icon-20" />} {busy ? 'Cancelling…' : 'Request Sent'}
      </Button>
    );
  }

  if (friendStatus === "requested") {
    if (!showDecisionActions) {
      return (
        <Button
          {...common}
          variant="outline"
          onClick={() => {
            try { document.getElementById('profile-controls')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch {}
          }}
          aria-label="Respond to friend request"
        >
          Respond
        </Button>
      );
    }
    return (
      <div className="flex items-center gap-2">
        <Button {...common} variant="success" onClick={async () => {
          if (busy) return; setBusy(true);
          try {
            setFriendStatus('friends');
            const url = isNumericId
              ? `/api/friendship/${encodeURIComponent(idStr)}`
              : `/api/user/v2/${encodeURIComponent(idStr)}/friend`;
            const res = await apiFetch(url, { method: 'PUT', body: JSON.stringify({ action: 'accept' }), cache: 'no-store' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const j = await res.json().catch(() => ({}));
            setFriendStatus((j?.status as FriendStatus) || 'friends');
            toast({ title: 'Friend request accepted', variant: 'success' });
          } catch (e: any) { setFriendStatus('requested'); toast({ title: 'Failed to accept request', description: e?.message || '', variant: 'destructive' }); } finally { setBusy(false); }
        }} aria-label="Accept friend request" aria-busy={busy}>
          {busy ? <Loader2 className="icon-20 animate-spin" /> : <Check className="icon-20" />} {busy ? 'Accepting…' : 'Accept'}
        </Button>
        <Button {...common} variant="destructive" onClick={async () => {
          if (busy) return; setBusy(true);
          try {
            setFriendStatus('none');
            const url = isNumericId
              ? `/api/friendship/${encodeURIComponent(idStr)}`
              : `/api/user/v2/${encodeURIComponent(idStr)}/friend`;
            const res = await apiFetch(url, { method: 'PUT', body: JSON.stringify({ action: 'decline' }), cache: 'no-store' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const j = await res.json().catch(() => ({}));
            setFriendStatus((j?.status as FriendStatus) || 'none');
            toast({ title: 'Friend request declined', variant: 'info' });
          } catch (e: any) { setFriendStatus('requested'); toast({ title: 'Failed to decline request', description: e?.message || '', variant: 'destructive' }); } finally { setBusy(false); }
        }} aria-label="Decline friend request" aria-busy={busy}>
          {busy ? <Loader2 className="icon-20 animate-spin" /> : null} Decline
        </Button>
      </div>
    );
  }

  // friends
  return (
    <Button
      {...common}
      variant="success"
      aria-label="You are friends. Click to remove"
      onClick={removeOrCancel}
      aria-busy={busy}
    >
      {busy ? <Loader2 className="icon-20 animate-spin" /> : <Check className="icon-20" />} {busy ? 'Removing…' : 'Friends'}
      <span className="sr-only">Remove friend</span>
    </Button>
  );
}

export default FriendButton;


